import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { ceil, isEmpty, pick, reduce } from 'lodash';

import { CLASS_NAME_OF_DISABLED_PAGE } from 'app-constants';
import { createPortalNode } from 'utils';

import './portal.scss';

interface Props {
    styleName?: string;
    className?: string;
    isPageDisabled?: boolean;
    replaceContent?: boolean;
    isFilled?: boolean;
    parentRef?: HTMLElement | null;
    parentNode?: HTMLElement | null;
    children: React.ReactNode;
}

type AnyVariables = { [key: string]: number };
type TrimmedVariables = Pick<AnyVariables, 'top' | 'left' | 'height'>;

interface Styles {
    transform?: string;
}

const Portal: React.FC<Props> = ({
    parentNode,
    className,
    isPageDisabled = true,
    parentRef,
    replaceContent = false,
    children,
    isFilled,
}) => {
    const el = useMemo(() => createPortalNode(), []);
    const ref = useRef<number>(0);

    useEffect(() => {
        if (parentNode) {
            if (replaceContent) {
                parentNode.innerHTML = '';
            }

            parentNode.appendChild(el);
        } else {
            document.body.appendChild(el);
        }

        return () => {
            if (parentNode) {
                parentNode.removeChild(el);
            } else {
                document.body.removeChild(el);
            }
        };
    }, [replaceContent, parentNode, el]);

    useEffect(() => {
        if (isPageDisabled) {
            document.body.classList.add(CLASS_NAME_OF_DISABLED_PAGE);
            ref.current = window.pageYOffset;
        }

        return () => {
            document.body.classList.remove(CLASS_NAME_OF_DISABLED_PAGE);

            if (isPageDisabled) {
                scrollToY(ref.current);
            }
        };
    }, [isPageDisabled]);

    return ReactDOM.createPortal(
        <div
            styleName={cn('container', {
                'is-filled': isFilled,
            })}
            className={cn('__app-Portal-container', className)}
            style={getPositionStyles(parentRef)}
        >
            {children}
        </div>,
        el
    );
};

function getPositionStyles(ref?: HTMLElement | null): Styles {
    if (!ref || !ref.getBoundingClientRect) return {};

    const originValues = ref.getBoundingClientRect();

    /* can not iterate through originValues */
    const iterableValues: TrimmedVariables = pick(originValues, [
        'left',
        'top',
        'height',
    ]);

    if (isEmpty(iterableValues)) return {};

    /* to avoid "blur" effect every variable must be integer */
    const roundedVariables: TrimmedVariables = reduce(
        iterableValues,
        (acc, val, key) => ({ ...acc, [key]: ceil(val) }),
        {} as TrimmedVariables
    );

    const { left, top, height } = roundedVariables;
    const scrolled = ceil(window.pageYOffset);
    const totalTopOffset = top + height + scrolled;

    return {
        transform: `translate3d(${left}px, ${totalTopOffset}px, 0)`,
    };
}

function scrollToY(yPosition: number): void {
    setTimeout(() => window.scrollTo(0, yPosition), 0);
}

export default React.memo(Portal);
